import type { MessageSegment } from "./types"

/**
 * Agrupa el razonamiento de un turno en UN panel, para renderizarlo.
 *
 * POR QUÉ EXISTE
 * --------------
 * `ChatMessage` pinta un `<Reasoning>` por cada segmento `reasoning`, así que un
 * turno con la forma real del backend —razonamiento, texto, razonamiento, texto…—
 * se ve como varios paneles «Razonamiento» sueltos. Eso es un problema de
 * presentación, y se arregla acá: al renderizar.
 *
 * QUÉ HACE, Y QUÉ NO
 * ------------------
 * Junta todos los segmentos `reasoning` en uno solo, en el lugar del primero, y
 * conserva TODO lo demás en su orden original.
 *
 * **Nunca mueve un `text` al panel de razonamiento.** El canal `reasoning` del
 * runtime ya trae el pensamiento y las notas de progreso de herramienta
 * (`_tool_progress_message` en `src/agents/promotores/app.py`); lo que llega por
 * `output` es salida del modelo —incluida la prosa entre herramientas, del tipo
 * «Tengo todos los datos, voy a construir…»— y esconderla dentro del panel
 * colapsado le quita al promotor texto que el agente sí escribió para él. No hay
 * forma estructural de distinguir esa prosa de la respuesta: deducirlo de «después
 * vino más razonamiento» es una heurística de posición, y el backend se niega a
 * clasificar por diseño (`app.py`, «No heuristic classification»).
 *
 * DÓNDE SE APLICA
 * ---------------
 * Sólo en el render, y a los tres casos por igual: turno vivo, turno recién
 * terminado y turno reabierto. Por eso los tres se ven idénticos. Los segmentos
 * que se guardan no se tocan — son el registro de lo que se transmitió, y tienen
 * que coincidir con lo que `infra-cdk/lambdas/agentcore_chat/turno.js` persiste
 * (`docs/guides/STREAMING_PROTOCOL.md` §3).
 *
 * Es idempotente y devuelve el arreglo recibido cuando no hay nada que agrupar,
 * así que se puede memoizar sin sorpresas.
 */
export function groupReasoningForDisplay(
  segments: MessageSegment[]
): MessageSegment[] {
  let primero = -1
  let cuantos = 0
  for (let i = 0; i < segments.length; i++) {
    if (segments[i].type === "reasoning") {
      if (primero === -1) primero = i
      cuantos += 1
    }
  }
  // Cero o un panel: no hay nada que agrupar. Devolver el mismo arreglo mantiene
  // estable la identidad para React y para cualquier memo.
  if (cuantos <= 1) return segments

  const unido = segments
    .filter(
      (seg): seg is Extract<MessageSegment, { type: "reasoning" }> =>
        seg.type === "reasoning"
    )
    .map(seg => seg.content)
    .join("\n\n")

  const salida: MessageSegment[] = []
  segments.forEach((seg, i) => {
    if (seg.type !== "reasoning") {
      salida.push(seg)
      return
    }
    // El panel único queda donde estaba el primero: el razonamiento abre el turno,
    // así que sale arriba y la respuesta se lee en su orden.
    if (i === primero) salida.push({ type: "reasoning", content: unido })
  })
  return salida
}
